import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { formatDate, formatTime, plural } from '../lib/format'
import StageBar from '../components/StageBar'
import { useEscClose } from '../lib/useEscClose'
import NewTicketDialog from './NewTicketDialog'
import {
  TICKET_STATUS_LABEL,
  ticketsApi,
  type PortalMe,
  type TicketDetail,
  type TicketKind,
  type TicketStatus,
} from './api'

/** Цвет статуса: открытые бросаются в глаза, закрытые приглушены. */
const STATUS_CLASS: Record<TicketStatus, string> = {
  new: 'tk-new',
  accepted: 'tk-accepted',
  in_progress: 'tk-progress',
  waiting_client: 'tk-waiting',
  resolved: 'tk-resolved',
  closed: 'tk-closed',
}

/**
 * Обращения или заявки — один экран, два назначения.
 *
 * Разделены, потому что спрашивают о них разное. У обращения важно, сколько
 * ждём ответа; у заявки — что с ней происходит и сколько она стоит. В общем
 * списке и то и другое тонет.
 */
export default function PortalTickets({
  me,
  kind,
  initialOpenId,
  onOpened,
}: {
  me: PortalMe
  kind: TicketKind
  /** Пришли из переписки — сразу раскрываем эту карточку */
  initialOpenId?: number | null
  onOpened?: () => void
}) {
  const [openOnly, setOpenOnly] = useState(true)
  const [openId, setOpenId] = useState<number | null>(initialOpenId ?? null)

  useEffect(() => {
    if (initialOpenId) {
      setOpenId(initialOpenId)
      // Сообщаем наверх, что просьбу выполнили: иначе карточка раскрывалась бы
      // снова при каждом возврате в раздел.
      onOpened?.()
    }
  }, [initialOpenId, onOpened])
  const [creating, setCreating] = useState(false)

  const list = useQuery({
    queryKey: ['portal', 'tickets', kind, openOnly],
    queryFn: () => ticketsApi.list(kind, openOnly),
  })

  const rows = list.data ?? []
  const заявка = kind === 'order'

  return (
    <>
      <h1 className="page-title">{заявка ? 'Заявки' : 'Обращения'}</h1>

      <p className="hint">
        {заявка
          ? 'Разовые работы: коды маркировки и всё остальное. Оплачиваются по факту выполнения.'
          : 'Вопросы в рамках сопровождения и аренды 1С — они уже покрыты вашим тарифом.'}
      </p>

      <div className="toolbar">
        <button className="btn primary" onClick={() => setCreating(true)}>
          {заявка ? '+ Новая заявка' : '+ Новое обращение'}
        </button>
        <button
          className={`btn${openOnly ? ' primary' : ''}`}
          onClick={() => setOpenOnly(!openOnly)}
          title="Скрыть решённые и закрытые"
        >
          {openOnly ? '✓ Только открытые' : 'Только открытые'}
        </button>
        <div style={{ flex: 1 }} />
        <div style={{ color: 'var(--muted)', paddingBottom: 6 }}>Всего: {rows.length}</div>
      </div>

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th className="col-num">№</th>
              <th>Вид</th>
              <th>Тема</th>
              <th>Кто обращался</th>
              <th>Состояние</th>
              <th>Обновлено</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && (
              <tr>
                <td colSpan={7} className="empty">
                  Загрузка…
                </td>
              </tr>
            )}
            {!list.isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="empty">
                  {заявка
                    ? openOnly
                      ? 'Открытых заявок нет'
                      : 'Заявок пока не было'
                    : openOnly
                      ? 'Открытых обращений нет'
                      : 'Обращений пока не было'}
                </td>
              </tr>
            )}
            {rows.map((t) => (
              <tr key={t.id}>
                <td className="task-num">{t.id}</td>
                <td className="nowrap">
                  <span className="tk-cat-chip">{t.category_title}</span>
                  {t.codes_count !== null && (
                    <span className="cell-sub">
                      {t.codes_count} {plural(t.codes_count, 'код', 'кода', 'кодов')}
                    </span>
                  )}
                  {t.direction && t.category !== 'codes' && (
                    <span className="cell-sub">{t.direction}</span>
                  )}
                </td>
                <td>
                  <button className="link-btn" onClick={() => setOpenId(t.id)}>
                    {t.subject}
                  </button>
                  {t.messages_count > 0 && (
                    <span className="cell-sub">сообщений: {t.messages_count}</span>
                  )}
                  {t.files_count > 0 && (
                    <span className="cell-sub">файлов: {t.files_count}</span>
                  )}
                </td>
                <td>{t.author_name ?? '—'}</td>
                <td>
                  <span className={`tk-chip ${STATUS_CLASS[t.status]}`}>
                    {TICKET_STATUS_LABEL[t.status]}
                  </span>
                  {t.stage_title && <span className="cell-sub">{t.stage_title}</span>}
                  {t.paid && <span className="cell-sub">оплачено</span>}
                  {t.overdue && (
                    <span className="cell-sub" style={{ color: '#b91c1c', fontWeight: 700 }}>
                      срок прошёл
                    </span>
                  )}
                </td>
                <td className="nowrap">{formatDate(t.updated_at)}</td>
                <td className="nowrap">
                  <button className="btn small" onClick={() => setOpenId(t.id)}>
                    Открыть
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {creating && (
        <NewTicketDialog kind={kind} onClose={() => setCreating(false)} onOpen={setOpenId} />
      )}
      {openId !== null && (
        <TicketDialog id={openId} me={me} onClose={() => setOpenId(null)} />
      )}
    </>
  )
}

/**
 * Карточка обращения или заявки в кабинете.
 *
 * Устроена как две колонки. Слева — переписка: ради неё карточку и открывают,
 * и она должна быть на виду, а не под всеми сведениями сразу. Справа — то, на
 * что смотрят молча: что сейчас происходит, кто ведёт, что готово забрать.
 *
 * Разговор прокручивается сам в себе, поэтому поле ответа всегда внизу окна:
 * раньше, чтобы написать, приходилось листать мимо всей переписки.
 */
function TicketDialog({
  id,
  me,
  onClose,
}: {
  id: number
  me: PortalMe
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const bottom = useRef<HTMLDivElement>(null)

  useEscClose(onClose)

  const ticket = useQuery({
    queryKey: ['portal', 'ticket', id],
    queryFn: () => ticketsApi.get(id),
    // Переписка живая: пока окно открыто, подтягиваем ответы сами.
    refetchInterval: 20_000,
  })

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['portal', 'ticket', id] })
    qc.invalidateQueries({ queryKey: ['portal', 'tickets'] })
  }

  const send = useMutation({
    mutationFn: () => ticketsApi.reply(id, text.trim()),
    onSuccess: () => {
      setText('')
      refresh()
    },
    onError: (e: any) => setError(e?.message ?? 'Не удалось отправить'),
  })

  const close = useMutation({
    mutationFn: () => ticketsApi.close(id),
    onSuccess: refresh,
    onError: (e: any) => setError(e?.message ?? 'Не получилось'),
  })

  const data = ticket.data
  const messages = data?.messages ?? []

  // Новое сообщение — вниз, к нему и прокручиваем
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [messages.length])

  const isClosed = data?.status === 'closed'

  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal huge tkw">
        <div className="tkw-head">
          <div className="tkw-titles">
            <div className="tkw-num">
              {data ? `${data.kind_title} № ${id}` : `№ ${id}`}
              {data && (
                <>
                  <span className="tk-cat-chip">{data.category_title}</span>
                  <span className={`tk-chip ${STATUS_CLASS[data.status]}`}>
                    {TICKET_STATUS_LABEL[data.status]}
                  </span>
                  {data.paid && <span className="tkw-ok">оплачено</span>}
                  {data.overdue && <span className="tkw-alarm">срок прошёл</span>}
                </>
              )}
            </div>
            {data && <div className="tkw-subject">{data.subject}</div>}
          </div>
          <button className="tkw-close" title="Закрыть окно" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && (
          <div className="error" style={{ margin: '10px 20px 0' }}>
            {error}
          </div>
        )}
        {ticket.isLoading && <p className="empty">Загрузка…</p>}

        {data && (
          <div className="tkw-body">
            {/* ---------------- слева: разговор ---------------- */}
            <div className="tkw-main">
              {data.steps.length > 0 && (
                <StageBar steps={data.steps} dueOn={data.due_on} overdue={data.overdue} />
              )}

              <div className="tkw-thread">
                {data.description && (
                  <div className="tkw-first">
                    <div className="tkw-first-label">Что просили сделать</div>
                    {data.description}
                  </div>
                )}
                {messages.length === 0 && (
                  <p className="empty" style={{ padding: '18px 0' }}>
                    Ответов пока нет. Мы напишем сюда — и продублируем на почту.
                  </p>
                )}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`tk-msg ${m.from_client ? 'tk-msg-mine' : 'tk-msg-theirs'}`}
                  >
                    <div className="tk-msg-head">
                      {m.author_name ?? (m.from_client ? 'Ваша организация' : 'Бизнес Эра')}
                      <span className="tk-msg-time">
                        {formatDate(m.created_at)} {formatTime(m.created_at)}
                      </span>
                    </div>
                    <div className="tk-msg-body">{m.body}</div>
                  </div>
                ))}
                <div ref={bottom} />
              </div>

              {isClosed ? (
                <p className="hint" style={{ marginBottom: 0 }}>
                  Запись закрыта. Если вопрос вернулся — заведите новую: так его
                  быстрее заметят.
                </p>
              ) : (
                <div className="tkw-reply">
                  <textarea
                    rows={3}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="Напишите ответ или уточнение — мы увидим сразу"
                  />
                  <button
                    className="btn primary"
                    disabled={send.isPending || !text.trim()}
                    onClick={() => {
                      setError('')
                      send.mutate()
                    }}
                  >
                    {send.isPending ? 'Отправляем…' : 'Отправить'}
                  </button>
                </div>
              )}
            </div>

            {/* ---------------- справа: что происходит ---------------- */}
            <div className="tkw-aside">
              <Block title="Что сейчас">
                <div className="tkw-state">
                  <Row label="Состояние" value={TICKET_STATUS_LABEL[data.status]} />
                  {data.stage_title && <Row label="Этап" value={data.stage_title} />}
                  {data.due_on && (
                    <Row label="Обещали к" value={formatDate(data.due_on)} alarm={data.overdue} />
                  )}
                  {data.agreed_hours !== null && (
                    <Row label="Согласовано" value={`${data.agreed_hours} ч`} />
                  )}
                  <Row
                    label="Оплата"
                    value={
                      data.paid
                        ? `оплачено${data.paid_at ? ` ${formatDate(data.paid_at)}` : ''}`
                        : data.kind === 'order'
                          ? 'после выполнения'
                          : 'входит в тариф'
                    }
                  />
                  <Row label="Ведёт" value={data.assignee_name ?? 'подбираем специалиста'} />
                </div>
              </Block>

              <Block title="Файлы">
                <Attachments ticket={data} onChanged={refresh} onError={setError} />
              </Block>

              <Block title="Подробности">
                <div className="tkw-facts">
                  {data.program && <span>{data.program}</span>}
                  {/* У обуви и духов товарная группа и вид совпадают —
                      показывать одно и то же дважды незачем */}
                  {data.direction && data.direction !== data.program && (
                    <span>{data.direction}</span>
                  )}
                  {data.codes_count !== null && (
                    <span className="tk-count-inline">
                      будет заказано {data.codes_count}{' '}
                      {plural(data.codes_count, 'код', 'кода', 'кодов')}
                    </span>
                  )}
                  <span>завёл {data.author_name ?? 'неизвестно кто'}</span>
                  <span>создано {formatDate(data.created_at)}</span>
                </div>
              </Block>

              {!isClosed && (
                <Block title="Вопрос решён?">
                  <p className="hint" style={{ marginTop: 0 }}>
                    {me.full_name}, закрывайте, когда всё сделано. Если вопрос
                    вернётся — заведите новую запись.
                  </p>
                  <button
                    className="btn"
                    disabled={close.isPending}
                    onClick={() => {
                      if (confirm('Закрыть? Вопрос считается решённым.')) close.mutate()
                    }}
                  >
                    {close.isPending ? 'Закрываем…' : 'Да, вопрос решён'}
                  </button>
                </Block>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="tkw-block">
      <div className="tkw-block-title">{title}</div>
      {children}
    </div>
  )
}

/** Строка «что — как»: подпись слева, значение справа. */
function Row({ label, value, alarm }: { label: string; value: string; alarm?: boolean }) {
  return (
    <div className="tkw-row">
      <span className="tkw-row-label">{label}</span>
      <span className={`tkw-row-value${alarm ? ' alarm' : ''}`}>{value}</span>
    </div>
  )
}


/**
 * Вложения к обращению: скриншот ошибки, акт, заполненный шаблон.
 *
 * Шаблон помечаем отдельно — по нему считали количество, и подменять его
 * молча нельзя: заказ уходит по тому файлу, который приложен к заявке.
 */
function Attachments({
  ticket,
  onChanged,
  onError,
}: {
  ticket: TicketDetail
  onChanged: () => void
  onError: (e: string) => void
}) {
  const [busy, setBusy] = useState(false)

  async function upload(file: File | undefined) {
    if (!file) return
    setBusy(true)
    onError('')
    try {
      await ticketsApi.attach(ticket.id, file)
      onChanged()
    } catch (e: any) {
      onError(e?.message ?? 'Не удалось приложить файл')
    } finally {
      setBusy(false)
    }
  }

  const closed = ticket.status === 'closed'
  // Через запасное значение: сервер может быть старее страницы и не отдать
  // список файлов. Это не повод ронять всю карточку.
  const все = ticket.files ?? []
  // Результат — отдельно и первым. В общем списке он тонет среди скриншотов
  // и накладных, а именно за ним человек и пришёл.
  const результаты = все.filter((f) => f.is_result)
  const files = все.filter((f) => !f.is_result)

  return (
    <>
      {результаты.length > 0 && (
        <div className="tk-result">
          <div className="tk-result-head">Готово — можно забирать</div>
          {результаты.map((f) => (
            <button
              key={f.id}
              type="button"
              className="btn primary"
              style={{ marginRight: 8, marginTop: 6 }}
              onClick={() =>
                void ticketsApi
                  .downloadAttachment(ticket.id, f.id, f.original_name)
                  .catch((e: any) => onError(e?.message ?? 'Не удалось скачать файл'))
              }
            >
              ↓ {f.original_name}
            </button>
          ))}
        </div>
      )}

    <div className="tk-files">
      {все.length === 0 && <p className="hint" style={{ margin: 0 }}>Файлов пока нет</p>}
      {files.map((f) => (
        <button
          key={f.id}
          type="button"
          className="tk-file"
          onClick={() =>
            void ticketsApi
              .downloadAttachment(ticket.id, f.id, f.original_name)
              .catch((e: any) => onError(e?.message ?? 'Не удалось скачать файл'))
          }
          title={`${Math.max(1, Math.round(f.size / 1024))} КБ${
            f.uploaded_by ? ` · приложил ${f.uploaded_by}` : ''
          }`}
        >
          {f.is_template ? '📊' : '📎'} {f.original_name}
        </button>
      ))}
      {!closed && (
        <label className="btn small tkw-file-add" style={{ cursor: 'pointer' }}>
          {busy ? 'Отправляем…' : '📎 Приложить файл'}
          <input
            type="file"
            style={{ display: 'none' }}
            disabled={busy}
            onChange={(e) => void upload(e.target.files?.[0])}
          />
        </label>
      )}
    </div>
    </>
  )
}
